// Firebase 消息推送模块（PWA 专用）
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { firebaseService } from './core';
import { vapidKey } from './config';
import user from 'service/user';

class FirebaseMessagingService {
    private static instance: FirebaseMessagingService;
    private messaging: Messaging | null = null;
    private initialized = false;

    private constructor() {}

    static getInstance(): FirebaseMessagingService {
        if (!FirebaseMessagingService.instance) {
            FirebaseMessagingService.instance = new FirebaseMessagingService();
        }
        return FirebaseMessagingService.instance;
    }

    // 初始化消息服务
    async initialize(): Promise<void> {
        if (this.initialized) {
            console.log('Firebase Messaging 已经初始化');
            return;
        }

        // 确保 Firebase 核心服务已初始化
        const app = firebaseService.getApp();
        if (!app) {
            console.error('Firebase 核心服务未初始化，无法初始化消息服务');
            return;
        }

        try {
            this.messaging = getMessaging(app);
            this.setupMessageHandler();
            this.initialized = true;
            console.log('Firebase Messaging 初始化成功');

            // 检查并注册 Service Worker
            await this.setupServiceWorker();

            // 检测操作系统
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
            console.log('设备类型:', isIOS ? 'iOS' : '非iOS');

            if (isIOS) {
                this.setupIOSNotificationTrigger();
            } else {
                await this.requestNotificationPermission();
            }
        } catch (error) {
            console.error('初始化 Firebase Messaging 失败:', error);
        }
    }

    // 设置消息处理器
    private setupMessageHandler(): void {
        if (!this.messaging) return;

        onMessage(this.messaging, payload => {
            console.log('收到前台消息:', payload);

            // 在前台显示通知
            if (Notification.permission === 'granted') {
                const notificationTitle = payload.notification?.title || '';
                const notificationOptions = {
                    body: payload.notification?.body || '',
                    icon: payload.notification?.icon || '',
                    data: payload.data,
                    requireInteraction: true,
                };

                // 使用 ServiceWorkerRegistration.showNotification() 显示通知
                navigator.serviceWorker.ready
                    .then(registration => {
                        registration.showNotification(notificationTitle, notificationOptions);
                    })
                    .catch(err => {
                        console.error('显示通知失败:', err);
                    });
            }

            // 处理应用内状态更新
            if (payload.data) {
                console.log('前台消息数据:', payload.data);
            }
        });
    }

    // 设置 Service Worker
    private async setupServiceWorker(): Promise<ServiceWorkerRegistration | null> {
        if (!('serviceWorker' in navigator)) {
            console.warn('此浏览器不支持 Service Worker');
            return null;
        }

        // 检查 Service Worker 文件是否存在
        this.ensureFirebaseServiceWorker();

        const swPaths = ['/firebase-messaging-sw.js'];
        
        for (const swPath of swPaths) {
            try {
                console.log(`尝试注册 Service Worker: ${swPath}...`);
                const registration = await navigator.serviceWorker.register(swPath, {
                    scope: '/',
                });
                console.log('Service Worker 注册成功:', registration);

                // 等待 Service Worker 激活
                await this.waitForServiceWorkerActivation(registration);
                
                return registration;
            } catch (error) {
                console.warn(`使用路径 ${swPath} 注册 Service Worker 失败:`, error);
            }
        }

        console.error('所有 Service Worker 注册尝试均失败');
        return null;
    }

    // 等待 Service Worker 激活
    private async waitForServiceWorkerActivation(registration: ServiceWorkerRegistration): Promise<void> {
        if (registration.installing || registration.waiting) {
            console.log('等待 Service Worker 激活...');

            return new Promise<void>(resolve => {
                const serviceWorker = registration.installing || registration.waiting;

                if (!serviceWorker) {
                    resolve();
                    return;
                }

                const listener = function () {
                    if (serviceWorker.state === 'activated') {
                        serviceWorker.removeEventListener('statechange', listener);
                        console.log('Service Worker 已成功激活');
                        resolve();
                    }
                };

                if (serviceWorker.state === 'activated') {
                    console.log('Service Worker 已经处于激活状态');
                    resolve();
                    return;
                }

                serviceWorker.addEventListener('statechange', listener);

                // 设置超时
                setTimeout(() => {
                    serviceWorker.removeEventListener('statechange', listener);
                    console.warn('等待 Service Worker 激活超时，继续执行');
                    resolve();
                }, 30000);
            });
        } else if (registration.active) {
            console.log('Service Worker 已经激活，无需等待');
        }
    }

    // 请求通知权限
    async requestNotificationPermission(): Promise<string | null> {
        if (!this.messaging) {
            console.error('Messaging 服务未初始化');
            return null;
        }

        try {
            // 检查 Notification API 是否可用
            if (!('Notification' in window)) {
                console.info('此浏览器不支持通知功能');
                return null;
            }

            // 检查当前通知权限状态
            if (Notification.permission === 'denied') {
                console.info('通知权限已被用户拒绝');
                return null;
            }

            // 如果权限未设置，先请求权限
            if (Notification.permission !== 'granted') {
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    console.info('未获得通知权限');
                    return null;
                }
            }

            // 获取 FCM 令牌
            const token = await this.getFCMToken();
            return token;
        } catch (error) {
            console.error('请求通知权限失败:', error);
            return null;
        }
    }

    // 获取 FCM 令牌
    private async getFCMToken(): Promise<string | null> {
        if (!this.messaging) return null;

        const maxRetries = 3;
        let retryCount = 0;

        while (retryCount <= maxRetries) {
            try {
                if (retryCount > 0) {
                    console.log(`尝试第${retryCount}次重试获取 FCM 令牌...`);
                    await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                }

                const currentToken = await getToken(this.messaging, { vapidKey });

                if (currentToken) {
                    console.info('获取令牌成功', currentToken);
                    
                    // 发送订阅请求到后端
                    console.log('发送 FCM 令牌到后端进行订阅');
                    await this.sendSubscribe(currentToken);
                    
                    return currentToken;
                } else {
                    console.warn(`获取令牌返回空值，可能需要重试 (${retryCount}/${maxRetries})`);
                    retryCount++;
                }
            } catch (error: any) {
                console.error(`获取令牌失败 (${retryCount}/${maxRetries}):`, error);

                if (error.message && error.message.includes('no active Service Worker')) {
                    console.log('检测到 Service Worker 相关错误，等待 Service Worker 准备就绪...');
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }

                retryCount++;

                if (retryCount > maxRetries) {
                    this.handleTokenError(error);
                    return null;
                }
            }
        }

        return null;
    }

    // 处理令牌错误
    private handleTokenError(error: any): void {
        if (error.code === 'messaging/permission-blocked') {
            console.info('通知权限被阻止。用户需要在浏览器设置中手动允许通知');
        } else if (error.message && error.message.includes('Registration failed - storage error')) {
            console.info('注册 Service Worker 时出现存储错误');
        } else if (error.message && error.message.includes('failed-service-worker-registration')) {
            console.info('Service Worker 注册失败');
        } else if (error.message && error.message.includes('no active Service Worker')) {
            console.info('没有活跃的 Service Worker');
        }
    }

    // 发送订阅
    private async sendSubscribe(token: string): Promise<boolean> {
        try {
            const res = await user.sendRegistrationTokens({ token });
            console.log('发送订阅请求成功:', res);
            return true;
        } catch (error) {
            console.error('发送订阅请求出错:', error);
            return false;
        }
    }

    // iOS 设备设置
    private setupIOSNotificationTrigger(): void {
        // 检查通知权限状态
        if (Notification.permission === 'granted') {
            console.log('iOS 设备已获得通知权限，尝试获取 FCM 令牌');
            this.requestNotificationPermission()
                .then(token => {
                    console.log('iOS 设备自动获取 FCM 令牌结果:', token ? '成功' : '失败');
                })
                .catch(error => {
                    console.error('iOS 设备自动获取 FCM 令牌错误:', error);
                });
            return;
        } else if (Notification.permission === 'denied') {
            console.log('通知权限已被拒绝');
            return;
        }

        // 使用 localStorage 记录是否已触发过请求
        const hasTriggered = localStorage.getItem('ios_notification_triggered');
        if (hasTriggered === 'true') {
            console.log('已经触发过 iOS 通知权限请求');
            return;
        }

        console.log('设置 iOS 通知点击触发器');

        // 点击事件处理函数
        const clickHandler = async () => {
            console.log('捕获到用户点击，尝试请求通知权限');
            document.removeEventListener('click', clickHandler);
            localStorage.setItem('ios_notification_triggered', 'true');

            try {
                const token = await this.requestNotificationPermission();
                console.log('iOS 设备通知权限请求结果:', token ? '成功' : '失败或被拒绝');
            } catch (error) {
                console.error('iOS 设备请求通知权限失败:', error);
            }
        };

        document.addEventListener('click', clickHandler);
    }

    // 检查并创建 Firebase Service Worker 文件
    private ensureFirebaseServiceWorker(): void {
        try {
            if (typeof window !== 'undefined') {
                fetch('/firebase-messaging-sw.js')
                    .then(response => {
                        if (!response.ok) {
                            console.warn('无法找到 Firebase Messaging Service Worker 文件');
                        } else {
                            console.log('Firebase Messaging Service Worker 文件已存在');
                        }
                    })
                    .catch(error => {
                        console.error('检查 Firebase Messaging Service Worker 文件时出错:', error);
                    });
            }
        } catch (error) {
            console.error('确保 Firebase Service Worker 存在时出错:', error);
        }
    }

    // 手动请求通知权限（供外部调用）
    async requestNotificationPermissionManually(): Promise<string | null> {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        console.log('手动请求通知权限，设备是否 iOS:', isIOS);

        // 获取或注册 Service Worker
        let swRegistration;
        if ('serviceWorker' in navigator) {
            try {
                swRegistration = await navigator.serviceWorker.getRegistration();
                if (!swRegistration) {
                    swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                        scope: '/',
                    });
                    console.log('手动请求时注册 Service Worker 成功');
                }
            } catch (error) {
                console.error('获取或注册 Service Worker 失败:', error);
            }
        }

        return await this.requestNotificationPermission();
    }

    // 获取 Messaging 实例
    getMessaging(): Messaging | null {
        return this.messaging;
    }

    // 检查是否已初始化
    isInitialized(): boolean {
        return this.initialized;
    }
}

// 导出单例实例
export const firebaseMessagingService = FirebaseMessagingService.getInstance();

// 导出初始化函数
export async function initFirebaseMessaging(): Promise<void> {
    return firebaseMessagingService.initialize();
}

// 导出手动请求权限函数
export async function requestNotificationPermissionManually(): Promise<string | null> {
    return firebaseMessagingService.requestNotificationPermissionManually();
}